import { Loader2Icon } from 'lucide-react';
import { FC } from 'react';
import { toast } from 'sonner';

import { SectionError } from '../../../../components/ui/error';
import { Route } from '../../../../constants';
import {
    useGetProductDetailsByProductVariationIdQuery,
    useUpdateProductDetailsByIdMutation,
} from '../../../../features/slice/products/product/productApiSlice';
import { toastError } from '../../../../utils';
import { ProductForm } from '../product-form';
import { useProductList } from '../product-list/product-list.hook';

type EditProductFormProps = {
    productVariationId: string;
    onSuccessCallback?: () => void;
};

export const EditProductForm: FC<EditProductFormProps> = ({
    productVariationId,
    onSuccessCallback,
}) => {
    const { data, isError, isFetching, isLoading, error } =
        useGetProductDetailsByProductVariationIdQuery(productVariationId);
    const { refetch } = useProductList();

    const [updateProductDetails, { isLoading: isUpdating }] =
        useUpdateProductDetailsByIdMutation();

    const handleSubmit = async (data: any) => {
        try {
            await updateProductDetails({
                id: productVariationId,
                brand_id: data.brand_id,
                generic_id: data.generic_id,
                strength_id: data.strength_id,
                type_id: data.type_id,
                unit_id: data.unit_id,
                name: data.name,
                description: data.description,
                alert_quantity: Number(data.alert_quantity),
                purchase_price: Number(data.purchase_price),
                wholesale_price: Number(data.wholesale_price),
                retail_price: Number(data.retail_price),
            }).unwrap();
            onSuccessCallback?.();
            refetch();

            toast.success('Product updated successfully');
        } catch (error) {
            toastError(error, 'EditProductForm: handleSubmit');
        }
    };

    if (isLoading || isFetching) {
        return (
            <div className="flex items-center justify-center p-10">
                <Loader2Icon className="mr-2 h-4 w-4 animate-spin" /> Loading...
            </div>
        );
    }

    if (isError) {
        return <SectionError error={error} fallbackPath={Route.PRODUCTS} />;
    }

    const initialValues = {
        brand_id: data?.data?.brand_id || '',
        brand_name: data?.data?.brand_name || '',
        generic_id: data?.data?.generic_id || '',
        generic_name: data?.data?.generic_name || '',
        strength_id: data?.data?.strength_id || '',
        strength_name: data?.data?.strength_name || '',
        type_id: data?.data?.type_id || '',
        type_name: data?.data?.type_name || '',
        unit_id: data?.data?.unit_id || '',
        unit_name: data?.data?.unit_name || '',

        name: data?.data?.product_name || '',
        description: data?.data?.description || '',
        alert_quantity: data?.data?.alert_quantity || 0,
        purchase_price: data?.data?.purchase_price || 0,
        wholesale_price: data?.data?.wholesale_price || 0,
        retail_price: data?.data?.retail_price || 0,
    };

    return (
        <div>
            <ProductForm
                initialValues={initialValues}
                onSubmit={handleSubmit}
                isLoading={isUpdating}
                title="Edit Product"
            />
        </div>
    );
};

EditProductForm.displayName = 'EditProductForm';
